import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";

import Copyright from "../../../components/Copyright";

import { useForgotPasswordMutation } from "../authApiSlice";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import LoginBackground from "../../../components/LoginBackground";
import Logo from "../../../components/Logo";
import ControlledTextField from "../../../components/controlled/ControlledTextField";
import { useGetCurrentBrandingQuery } from "../../branding/brandingApiSlice";
import { getFormSchema } from "./formSchema";

export default function ForgotPassword() {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});
	const { data: branding } = useGetCurrentBrandingQuery();

	const onSubmit = async ({ email }) => {
		await forgotPassword({ email })
			.unwrap()
			.then((payload) => {
				enqueueSnackbar(t("reset_link_has_been_send_via_email"), {
					variant: "success",
					preventDuplicate: true,
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				if (err.message) {
					enqueueSnackbar(err.message, {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
		}
	}, [errors, enqueueSnackbar]);

	const shouldUseBrandedTheme =
		branding?.loginBoxColor ||
		branding?.loginBgColor ||
		branding?.barColor ||
		branding?.buttonColor;

	return (
		<Grid container component="main" sx={{ height: "100vh" }}>
			<CssBaseline />
			<LoginBackground>
				<Box>
					<Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
						<Logo />
					</Box>
					<Box
						component="form"
						noValidate
						onSubmit={handleSubmit(onSubmit)}
						sx={{
							pt: 5,
							pb: 7,
							px: 6,
							borderRadius: 0,
							background: (theme) => theme.palette.loginBox,
							width: "442px",
						}}
					>
						<ControlledTextField
							sx={{
								"& .MuiInputBase-root": { background: "#fff !important" },
							}}
							shrinkLabel={false}
							variant="filled"
							margin="normal"
							control={control}
							name="email"
							label={t("email_label")}
							error={!!errors?.email}
							autoFocus
							type="email"
							required
						/>
						<Grid container>
							<Grid size="grow">
								<Link to="/login" style={{ color: "#fff" }}>
									{t("already_have_account")}
								</Link>
							</Grid>
							{!shouldUseBrandedTheme && (
								<Grid item>
									<Link to="/register" style={{ color: "#fff" }}>
										{t("create_account")}
									</Link>
								</Grid>
							)}
						</Grid>
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<Button
								loading={isLoading}
								type="submit"
								variant="contained"
								sx={{ mt: 3 }}
							>
								{t("remind_password")}
							</Button>
						</Box>
					</Box>
				</Box>
				<Copyright sx={{ mt: 8, mb: 4 }} />
			</LoginBackground>
		</Grid>
	);
}
